import { memo, useRef, useState } from 'react';
import { Link, router } from '@inertiajs/react';
import { Card } from '../../../components/ui';
import Logo from '../../../components/ui/Logo';
import ThemeToggle from '../../../components/ui/ThemeToggle';
import VocabActivity from '../../../components/vocabulary/VocabActivity';
import ChapterReader from '../../../components/reader/ChapterReader';
import LiveCursorOverlay from '../../../components/live_sessions/LiveCursorOverlay';
import LiveSessionController from '../../../components/live_sessions/LiveSessionController';
import RemoteHighlights from '../../../components/live_sessions/RemoteHighlights';
import RemoteHoverFocus from '../../../components/live_sessions/RemoteHoverFocus';
import PresencePanel from '../../../components/live_sessions/PresencePanel';
import JoinBanner from '../../../components/live_sessions/JoinBanner';
import RefocusToast from '../../../components/live_sessions/RefocusToast';
import { useHighlightSync } from '../../../components/live_sessions/useHighlightSync';
import { useFocusBroadcast } from '../../../components/live_sessions/useFocusBroadcast';
import { useScrollFollow } from '../../../components/live_sessions/useScrollSync';
import {
  usePinFollow,
  useReaderLock,
} from '../../../components/live_sessions/useChapterAnchorSync';
import { usePresenceAway } from '../../../components/live_sessions/usePresenceAway';
import { NavIcon } from '../../../components/layout/Sidebar';
import { useLivePresenceChannel } from '../../../hooks/useLivePresenceChannel';

const VIEW_LABELS = {
  VocabularyPractice: 'Vocabulary Practice',
  ChapterReader: 'Chapter Reader',
  SentenceStudio: 'Sentence Studio',
  CloseReading: 'Close Reading',
};

export default function StudentLiveSessionLobby({
  live_session,
  student,
  teacher,
  participants = [],
  activity,
  activity_error,
}) {
  const [status, setStatus] = useState(live_session.status);
  const [currentView, setCurrentView] = useState(live_session.current_view);
  const [cursors, setCursors] = useState({});
  const [highlights, setHighlights] = useState({});
  const [focuses, setFocuses] = useState({});
  const [aways, setAways] = useState({});
  const [stepSignal, setStepSignal] = useState(null);
  // Latest teacher-driven scroll position; ts makes each receive a fresh
  // object so useScrollFollow re-applies even on a repeated percentage.
  const [scrollSync, setScrollSync] = useState(null);
  // Chapter Reader: the mirrored chapter the teacher has open (fed entirely by
  // the teacher's broadcasts), the latest paragraph anchor, and whether our
  // reader is hard-locked to the teacher. Locked by default for a guided read.
  const [readerState, setReaderState] = useState(null); // { chapter_id, chapter, book, html, analyzed_sentences, selections }
  const [readerPin, setReaderPin] = useState(null); // { para, ts }
  const [readerLocked, setReaderLocked] = useState(true);
  const readerScrollRef = useRef(null);
  const readerContentRef = useRef(null);
  // Bumped on each local hidden→visible transition to fire RefocusToast.
  const [refocusKey, setRefocusKey] = useState(null);
  // Teacher → student directive received over the cable. Stamped with
  // a fresh id on every receive so OnboardingActivity's effect re-runs
  // even when the same action arrives twice.
  const [peerIntent, setPeerIntent] = useState(null);
  // Live ghost_count per user_id. Seeded from server-rendered props,
  // updated from the session_state participants snapshot on subscribe,
  // and from the away_state broadcast's optional ghost_count field
  // (present only on the server's increment-on-return).
  const [ghostCounts, setGhostCounts] = useState(() => {
    const seed = {};
    participants.forEach((p) => {
      if (p?.id != null) seed[p.id] = p.ghost_count ?? 0;
    });
    if (teacher?.id != null) seed[teacher.id] = teacher.ghost_count ?? 0;
    return seed;
  });
  const activityRootRef = useRef(null);

  const refreshPresence = () =>
    router.reload({
      only: ['participants', 'teacher'],
      preserveScroll: true,
    });

  const { perform, isConnected } = useLivePresenceChannel(live_session.id, {
    onSessionState: (data) => {
      setStatus(data.status);
      setCurrentView(data.current_view);
      if (Array.isArray(data.participants)) {
        setGhostCounts((prev) => {
          const next = { ...prev };
          data.participants.forEach((p) => {
            if (p?.user_id != null) next[p.user_id] = p.ghost_count ?? 0;
          });
          return next;
        });
      }
    },
    onSetView: (data) => {
      setCurrentView(data.view);
    },
    onSessionStatus: (data) => {
      setStatus(data.status);
    },
    onPresence: refreshPresence,
    onCursor: (data) => {
      setCursors((prev) => ({ ...prev, [data.user_id]: data }));
    },
    onHighlight: (data) => {
      setHighlights((prev) => ({ ...prev, [data.user_id]: data }));
    },
    onStep: (data) => {
      setStepSignal({ direction: data.direction, ts: Date.now() });
    },
    onScrollSync: (data) => {
      setScrollSync({ top_pct: data.top_pct, ts: Date.now() });
    },
    onReaderPin: (data) => {
      setReaderPin({ para: data.para, ts: Date.now() });
    },
    onReaderLock: (data) => {
      setReaderLocked(!!data.locked);
    },
    onSetChapter: (data) => {
      setReaderState({
        chapter_id: data.chapter_id,
        chapter: data.chapter,
        book: data.book || null,
        html: data.html || '',
        analyzed_sentences: data.analyzed_sentences || [],
        selections: data.selections || [],
      });
      setReaderPin(null); // new chapter resets the pin to the top
    },
    onReaderSelections: (data) => {
      setReaderState((prev) => {
        if (!prev) return prev;
        // Coerce: chapter ids cross the cable as JSON numbers but be defensive.
        if (String(prev.chapter_id) !== String(data.chapter_id)) return prev;
        return { ...prev, selections: data.selections || [] };
      });
    },
    onFocus: (data) => {
      setFocuses((prev) => ({
        ...prev,
        [data.user_id]: { key: data.key, role: data.role, name: data.name },
      }));
    },
    onAway: (data) => {
      setAways((prev) => ({
        ...prev,
        [data.user_id]: { away: !!data.away, role: data.role, name: data.name },
      }));
      // Server-broadcast ghost_count only on the away→present transition
      // for a student. When present, it's the authoritative new value —
      // overwrite (not accumulate).
      if (data.ghost_count != null) {
        setGhostCounts((prev) => ({ ...prev, [data.user_id]: data.ghost_count }));
      }
    },
    onPeerIntent: (data) => {
      // Channel only forwards teacher-originated peer_intent. Stamp with
      // id so the consumer effect re-runs even when the same action
      // (e.g. set_stage1_hint with open: true) arrives twice in a row.
      setPeerIntent({ action: data.action, payload: data.payload, id: Date.now() });
    },
  });

  const isChapter = currentView === 'ChapterReader';

  // Transient text-match highlight sync mutates the chapter DOM — disable it
  // (and RemoteHighlights below) for the chapter view; authored highlights are
  // mirrored via reader_selections instead.
  useHighlightSync(isChapter ? null : perform);
  useFocusBroadcast(perform);
  // Student follows the teacher's scroll position (teacher-only broadcast).
  // The Chapter Reader uses paragraph-anchor follow instead, so disable the
  // page-level percentage follow for that view.
  useScrollFollow(scrollSync, !isChapter);
  // Chapter Reader: follow the teacher's paragraph anchor, and hard-lock our
  // scroll container while the teacher hasn't released us.
  usePinFollow(readerPin, isChapter, readerContentRef, readerScrollRef);
  useReaderLock(readerContentRef, readerLocked, isChapter, readerScrollRef, readerState?.chapter_id);
  usePresenceAway(perform, isConnected, true, ({ from, to }) => {
    // Local hidden→visible transition. Bump the refocus toast key.
    // (Server-side ghost_count increment lands in a separate commit.)
    if (from === true && to === false) setRefocusKey(Date.now());
  });

  // Forward our local activity state to the teacher's observer mirror.
  const broadcastActivityState = (state) => {
    perform('progress_state', { state });
  };

  let body;
  if (status === 'completed') {
    body = (
      <CenterMessage
        emoji="✓"
        title="Session ended"
        body="Your teacher has ended this session."
      />
    );
  } else if (isChapter && readerState?.html) {
    body = (
      <StudentChapterReader
        readerState={readerState}
        pinPara={readerPin?.para ?? null}
        scrollRef={readerScrollRef}
        contentRef={readerContentRef}
      />
    );
  } else if (isChapter) {
    body = (
      <CenterMessage
        emoji="…"
        title="Waiting for your teacher"
        body="Your teacher will open a chapter shortly."
        emojiPulse
      />
    );
  } else if (currentView === 'VocabularyPractice' && activity) {
    body = (
      <div ref={activityRootRef} data-activity-root>
        <VocabActivity
          {...activity}
          stepSignal={stepSignal}
          onStateChange={broadcastActivityState}
          peerIntent={peerIntent}
          onDone={() => router.visit('/student/dashboard')}
        />
      </div>
    );
  } else if (currentView === 'VocabularyPractice' && activity_error) {
    body = (
      <CenterMessage
        title="Couldn’t start activity"
        body={activity_error}
      />
    );
  } else {
    body = (
      <CenterMessage
        emoji="…"
        title="Waiting for teacher"
        body="Your teacher will start an activity shortly."
        emojiPulse
      />
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-950">
      <StudentLiveTopBar
        student={student}
        viewLabel={VIEW_LABELS[currentView]}
        bookTitle={isChapter ? readerState?.book?.title : null}
        chapterLabel={isChapter ? (readerState?.chapter?.formatted_label || readerState?.chapter?.title) : null}
      />

      <main className="flex-1 flex flex-col min-h-0">{body}</main>

      <LiveSessionController
        role="student"
        status={status}
        peer={teacher}
        participants={participants}
      />

      <PresencePanel
        participants={panelParticipants({ participants, teacher, student, aways, ghostCounts })}
        currentUserId={student?.id}
      />

      <JoinBanner sessionId={live_session.id} active={status === 'active'} />

      <RefocusToast triggerKey={refocusKey} />

      <LiveCursorOverlay
        cursors={cursors}
        focuses={focuses}
        aways={aways}
        perform={perform}
        selfUserId={student?.id}
      />

      {/* Paint the teacher's live text selection on the student (chapter view
          included), like the vocab live session. The student never paints its
          own (self is filtered inside RemoteHighlights). */}
      <RemoteHighlights highlights={highlights} selfUserId={student?.id} />

      <RemoteHoverFocus focuses={focuses} selfUserId={student?.id} />
    </div>
  );
}

// Merge server-rendered participant rows with live away_state and the
// live ghost_count map so the PresencePanel renders a stable two-row
// view (teacher + student) regardless of which side the lobby is on.
// Falls back to the `teacher` and `student` props when the participant
// list doesn't yet include both rows (e.g. the student joined before
// the teacher hit the page).
function panelParticipants({ participants, teacher, student, aways, ghostCounts }) {
  const byId = new Map();
  participants.forEach((p) => {
    if (p?.id != null) byId.set(p.id, { ...p });
  });
  if (teacher?.id != null && !byId.has(teacher.id)) {
    byId.set(teacher.id, { ...teacher, role: 'teacher' });
  }
  if (student?.id != null && !byId.has(student.id)) {
    byId.set(student.id, {
      id: student.id,
      full_name: student.full_name || student.first_name,
      initials: student.initials,
      role: 'student',
    });
  }
  return Array.from(byId.values()).map((p) => ({
    ...p,
    away: !!aways[p.id]?.away,
    ghost_count: ghostCounts[p.id] ?? p.ghost_count ?? 0,
  }));
}

function StudentLiveTopBar({ student, viewLabel, bookTitle, chapterLabel }) {
  return (
    <header className="sticky top-0 flex-shrink-0 h-14 bg-white/80 backdrop-blur-md border-b border-gray-200 dark:bg-gray-900/80 dark:border-gray-700 z-30">
      <div className="flex items-center justify-between h-full px-4">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <Link href="/student/dashboard" className="flex items-center gap-2 flex-shrink-0">
            <Logo className="h-9" />
          </Link>
          <div className="pl-3 border-l border-gray-400 dark:border-gray-400 min-w-0 flex items-center gap-2">
            <NavIcon
              iconKey="liveSession"
              className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0"
            />
            {bookTitle ? (
              // While reading, show what we're reading rather than the generic
              // "Live Session · Chapter Reader" label.
              <div className="min-w-0 flex items-baseline gap-2">
                <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                  {bookTitle}
                </span>
                {chapterLabel && (
                  <>
                    <span className="text-gray-400 dark:text-gray-500 flex-shrink-0">·</span>
                    <span className="text-sm text-gray-600 dark:text-gray-400 truncate">
                      {chapterLabel}
                    </span>
                  </>
                )}
              </div>
            ) : (
              <>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                  Live Session
                </span>
                {viewLabel && (
                  <>
                    <span className="text-gray-400 dark:text-gray-500">·</span>
                    <span className="text-sm text-gray-600 dark:text-gray-400 truncate">
                      {viewLabel}
                    </span>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          <ThemeToggle />
          {student && (
            <div className="flex items-center gap-2">
              <span className="hidden sm:inline text-sm text-gray-600 dark:text-gray-400">
                {student.first_name}
              </span>
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-600 dark:to-gray-700 flex items-center justify-center text-xs font-medium text-gray-700 dark:text-gray-200">
                {student.initials || student.first_name?.[0] || '?'}
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

// The student's mirror of the teacher's chapter, in the same framed reader.
// Memoized so the lobby's high-frequency re-renders (cursor moves, presence)
// don't re-render it — re-rendering on every tick was wiping the imperatively
// applied pin highlight + painted selections (the teacher keeps them because
// its BookReader is likewise memoized). Re-renders only when the mirrored
// chapter/selections or the pin actually change.
//
// The scroll container uses a concrete max-height (not a flex chain) so it is
// reliably THE scroller — which makes the hard lock (overflow:hidden) actually
// stop the student, and makes programmatic pin-scroll land here rather than on
// the window.
const StudentChapterReader = memo(function StudentChapterReader({
  readerState,
  pinPara,
  scrollRef,
  contentRef,
}) {
  return (
    <div className="px-4 sm:px-6 py-6">
      {/* Balanced side columns keep the reading frame in the true center while
          the title sits in the left margin (mirroring the teacher's TOC side).
          The equal-width right spacer is what keeps the page centered. */}
      <div className="flex justify-center gap-6">
        <aside className="hidden lg:block w-56 flex-shrink-0">
          <div className="sticky top-1">
            {readerState.book?.title && (
              <p className="text-sm text-gray-500 dark:text-gray-400">{readerState.book.title}</p>
            )}
            <p className="text-lg font-medium text-gray-900 dark:text-white mt-1">
              {readerState.chapter?.formatted_label || readerState.chapter?.title}
            </p>
          </div>
        </aside>
        <div className="w-full max-w-2xl">
          <div
            ref={scrollRef}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 max-h-[calc(100vh-12rem)] overflow-y-auto"
          >
            <div ref={contentRef} className="px-8 sm:px-10 py-8">
              <ChapterReader
                readOnly
                book={null}
                chapter={readerState.chapter}
                html={readerState.html}
                initialSelections={readerState.selections}
                analyzedSentences={readerState.analyzed_sentences}
                focusedParagraphIndex={pinPara ?? null}
              />
            </div>
          </div>
        </div>
        <div className="hidden lg:block w-56 flex-shrink-0" aria-hidden="true" />
      </div>
    </div>
  );
});

function CenterMessage({ emoji, title, body, emojiPulse }) {
  return (
    <div className="flex-1 flex items-center justify-center p-6">
      <Card className="p-8 text-center max-w-md">
        {emoji && (
          <div className="mx-auto w-12 h-12 mb-4 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
            <span className={`text-2xl ${emojiPulse ? 'animate-pulse' : ''}`}>{emoji}</span>
          </div>
        )}
        <h1 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">{title}</h1>
        {body && <p className="text-sm text-gray-500 dark:text-gray-400">{body}</p>}
      </Card>
    </div>
  );
}
